import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import {
  currentDisplayCurrency,
  fromUsd,
  formatDisplayCurrency,
} from '@/lib/displayCurrency';
import { CurrencySwitcher } from '@/components/layout/CurrencySwitcher';

type PlanKey = 'free' | 'growth' | 'global' | 'enterprise';
type AiBand = 'light' | 'medium' | 'heavy';

interface Recommendation {
  plan: PlanKey;
  seatPriceUsd: number;
  aiAddOnUsd: number;
  reason: string;
}

const SEAT_PRICE: Record<PlanKey, number> = {
  free: 0,
  growth: 39,
  global: 89,
  enterprise: 169,
};

// Per-user/month AI passthrough cost at provider rates. Heavy = full
// reasoning on every signal / keigo-compose call. Light = Q&A only.
const AI_ADDON: Record<AiBand, number> = {
  light: 2,
  medium: 8,
  heavy: 22,
};

/**
 * Pick the right tier for a (seats, regions, ai_band) combo.
 * Rules:
 *  - 5 seats or fewer + 1 region + light AI → Free
 *  - 1 region only → Growth
 *  - 2+ regions OR medium AI → Global
 *  - signal alerts heavy / enterprise SLA needed / SSO → Enterprise
 *    (the calculator triggers Enterprise when seats > 50 OR aiBand=heavy
 *     since both indicate org-tier needs)
 */
function pickPlan(seats: number, regions: number, ai: AiBand): Recommendation {
  if (seats > 50 || ai === 'heavy') {
    return {
      plan: 'enterprise',
      seatPriceUsd: SEAT_PRICE.enterprise,
      aiAddOnUsd: AI_ADDON[ai],
      reason: ai === 'heavy' ? 'reason_heavy_ai' : 'reason_large_team',
    };
  }
  if (regions >= 2 || ai === 'medium') {
    return {
      plan: 'global',
      seatPriceUsd: SEAT_PRICE.global,
      aiAddOnUsd: AI_ADDON[ai],
      reason: regions >= 2 ? 'reason_multi_region' : 'reason_medium_ai',
    };
  }
  if (seats <= 5 && ai === 'light') {
    return { plan: 'free', seatPriceUsd: 0, aiAddOnUsd: 0, reason: 'reason_free_tier' };
  }
  return {
    plan: 'growth',
    seatPriceUsd: SEAT_PRICE.growth,
    aiAddOnUsd: AI_ADDON[ai],
    reason: 'reason_growth_default',
  };
}

const REGIONS = [
  { code: 'JP', labelKey: 'pricing_calc.region_jp' },
  { code: 'US', labelKey: 'pricing_calc.region_us' },
  { code: 'EU', labelKey: 'pricing_calc.region_eu' },
  { code: 'SG', labelKey: 'pricing_calc.region_sg' },
  { code: 'KR', labelKey: 'pricing_calc.region_kr' },
];

const AI_OPTIONS: Array<{ value: AiBand; labelKey: string; hintKey: string }> = [
  { value: 'light', labelKey: 'pricing_calc.ai_light', hintKey: 'pricing_calc.ai_light_hint' },
  { value: 'medium', labelKey: 'pricing_calc.ai_medium', hintKey: 'pricing_calc.ai_medium_hint' },
  { value: 'heavy', labelKey: 'pricing_calc.ai_heavy', hintKey: 'pricing_calc.ai_heavy_hint' },
];

export function PricingCalculator() {
  const { t, i18n } = useTranslation();
  const [seats, setSeats] = useState<number>(10);
  const [regions, setRegions] = useState<string[]>(['JP']);
  const [ai, setAi] = useState<AiBand>('medium');
  const [currency, setCurrency] = useState(currentDisplayCurrency());

  const rec = useMemo(() => pickPlan(seats, regions.length, ai), [seats, regions.length, ai]);

  const monthlyUsd = useMemo(() => {
    return seats * (rec.seatPriceUsd + rec.aiAddOnUsd);
  }, [seats, rec.seatPriceUsd, rec.aiAddOnUsd]);

  const annualUsd = monthlyUsd * 12;
  const monthly = formatDisplayCurrency(fromUsd(monthlyUsd, currency).amount, currency, i18n.language);
  const annual = formatDisplayCurrency(fromUsd(annualUsd, currency).amount, currency, i18n.language);

  function toggleRegion(code: string) {
    setRegions((prev) =>
      prev.includes(code) ? prev.filter((r) => r !== code) : [...prev, code],
    );
  }

  return (
    <section className="border border-ink-200 rounded-md overflow-hidden bg-white">
      <header className="bg-ink-50 px-6 md:px-8 py-5 border-b border-ink-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.22em] text-ink-500 font-semibold">
            {t('pricing_calc.eyebrow')}
          </p>
          <h2 className="display serif-jp mt-2 text-xl md:text-2xl font-medium tracking-tight text-ink-900">
            {t('pricing_calc.title')}
          </h2>
        </div>
        <CurrencySwitcher onChange={setCurrency} />
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
        {/* Left — inputs */}
        <div className="lg:col-span-7 px-6 md:px-8 py-7 space-y-7 border-b lg:border-b-0 lg:border-r border-ink-200">
          {/* Seats */}
          <div>
            <div className="flex items-baseline justify-between">
              <label htmlFor="seats" className="text-sm font-semibold text-ink-900">
                {t('pricing_calc.seats_label')}
              </label>
              <span className="num text-2xl font-medium text-ink-900 tabular-nums">
                {seats}
                <span className="text-[10.5px] uppercase tracking-[0.18em] text-ink-500 ml-2">
                  {t('pricing_calc.seats_unit')}
                </span>
              </span>
            </div>
            <input
              id="seats"
              type="range"
              min={1}
              max={200}
              step={1}
              value={seats}
              onChange={(e) => setSeats(Number(e.target.value))}
              aria-label={t('pricing_calc.seats_label')}
              className="mt-3 w-full accent-accent-500"
            />
            <div className="mt-1 flex justify-between text-[10.5px] uppercase tracking-[0.16em] text-ink-400 font-medium">
              <span className="num">1</span>
              <span className="num">50</span>
              <span className="num">100</span>
              <span className="num">200</span>
            </div>
          </div>

          {/* Regions */}
          <div>
            <label className="block text-sm font-semibold text-ink-900 mb-3">
              {t('pricing_calc.regions_label')}
            </label>
            <div className="flex flex-wrap gap-2">
              {REGIONS.map((r) => {
                const active = regions.includes(r.code);
                return (
                  <button
                    type="button"
                    key={r.code}
                    onClick={() => toggleRegion(r.code)}
                    className={
                      'text-xs px-3 py-1.5 rounded-md border transition ' +
                      (active
                        ? 'border-accent-500 bg-accent-50 text-accent-700 font-semibold'
                        : 'border-ink-300 text-ink-700 hover:bg-ink-100')
                    }
                  >
                    {t(r.labelKey)} ({r.code})
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-[12px] text-ink-500">
              {regions.length} {t('pricing_calc.regions_count')}
            </p>
          </div>

          {/* AI volume */}
          <div>
            <label className="block text-sm font-semibold text-ink-900 mb-3">
              {t('pricing_calc.ai_label')}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {AI_OPTIONS.map((opt) => {
                const active = ai === opt.value;
                return (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => setAi(opt.value)}
                    className={
                      'text-left px-3 py-3 rounded-md border transition ' +
                      (active
                        ? 'border-accent-500 bg-accent-50 ring-1 ring-accent-500/30'
                        : 'border-ink-300 hover:bg-ink-100')
                    }
                  >
                    <span className={'block text-sm font-semibold ' + (active ? 'text-accent-700' : 'text-ink-900')}>
                      {t(opt.labelKey)}
                    </span>
                    <span className="block mt-1 text-[11.5px] text-ink-500 leading-snug">
                      {t(opt.hintKey)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right — recommendation */}
        <div className="lg:col-span-5 bg-ink-950 text-ink-50 px-6 md:px-8 py-7 flex flex-col justify-between">
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.22em] text-ink-400 font-semibold">
              {t('pricing_calc.recommendation')}
            </p>
            <p className="display serif-jp mt-2 text-3xl md:text-4xl font-medium tracking-tight">
              {t(`pricing.${rec.plan}`)}
            </p>
            <p className="mt-2 text-[13px] text-ink-300 leading-relaxed">
              {t(`pricing_calc.${rec.reason}`)}
            </p>
          </div>

          <div className="mt-6">
            <div className="flex items-baseline gap-2 border-t border-ink-800 pt-5">
              <span className="num text-4xl md:text-5xl font-medium text-white tabular-nums">
                {monthly}
              </span>
              <span className="text-[11px] uppercase tracking-[0.18em] text-ink-400">
                {t('pricing_calc.per_month')}
              </span>
            </div>
            <p className="mt-2 text-[12.5px] text-ink-400 num">
              {t('pricing_calc.annual_prefix')} {annual}
            </p>

            {/* Breakdown */}
            {rec.plan !== 'free' && (
              <dl className="mt-5 space-y-1.5 text-[12.5px] text-ink-300 font-mono">
                <div className="flex justify-between">
                  <dt>
                    {seats} × {formatDisplayCurrency(fromUsd(rec.seatPriceUsd, currency).amount, currency, i18n.language)}
                    /seat
                  </dt>
                  <dd className="num">
                    {formatDisplayCurrency(fromUsd(seats * rec.seatPriceUsd, currency).amount, currency, i18n.language)}
                  </dd>
                </div>
                {rec.aiAddOnUsd > 0 && (
                  <div className="flex justify-between">
                    <dt>
                      AI ({t(`pricing_calc.ai_${ai}`)}) × {seats}
                    </dt>
                    <dd className="num">
                      {formatDisplayCurrency(fromUsd(seats * rec.aiAddOnUsd, currency).amount, currency, i18n.language)}
                    </dd>
                  </div>
                )}
              </dl>
            )}

            <Link
              to={`/signup?plan=${rec.plan}`}
              className="mt-7 inline-flex w-full h-11 items-center justify-center gap-2 rounded-md bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 transition-colors"
            >
              {t('pricing_calc.cta_start')} <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="mt-3 text-[11px] text-ink-500 text-center">
              {t('pricing_calc.cta_foot')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
